import PropTypes from "prop-types";

import HorizontalLine from "@/components/data-display/HorizontalLine";
import ProductReview from "@/components/data-display/card/ProductReview";
import Rating from "@/components/data-display/Rating";
import Pagination from "@/components/pagination";
import { calculatePaginationIndices } from "@/utils/helpers/pagination";
import InlineLoader from "@/components/loader/InlineLoader";
import { useState } from "react";
import { useGetProductReview } from "@/api/client/product";
import { useRouter } from "next/router";

function ProductReviewSection({ productReviewData }) {
  const [page, setPage] = useState(1);

  const [reviews, setReviews] = useState(productReviewData);

  const [isPaginating, setPaginating] = useState(false);
  const {
    query: { slug },
  } = useRouter();

  const { totalItems, currentPage, perPage, totalPage } =
    calculatePaginationIndices(reviews?.meta);

  const {
    average_product_rating,
    total_rating_count,
    items: reviewList,
  } = reviews || {};

  const { isFetching: isReviewFetching } = useGetProductReview({
    enabled: isPaginating,
    query: { slug, page },
    onSuccess: (data) => {
      setReviews((prevState) => ({ ...prevState, ...data.reviews }));
    },
  });

  const handlePagination = (page) => {
    setPage(page + 1);
    if (!isPaginating) setPaginating(true);
  };

  return (
    <section className="relative">
      {isReviewFetching && (
        <div className="absolute inset-0  bg-white/50">
          <InlineLoader className="w-[5rem] h-[5rem]" />
        </div>
      )}

      <div>
        <div className="space-y-[1.125rem]">
          <h3 className="text-[1.25rem] font-semibold text-grayDisplay-900">
            Reviews {total_rating_count > 0 && `(${total_rating_count})`}
          </h3>
          <span className="block text-[0.938rem] text-grayDisplay-850">
            Get specific details about this product from customers who own it.
          </span>
        </div>
        {reviewList?.length > 0 && (
          <div className="mt-[2.25rem] space-y-[1.125rem]">
            <p className="text-grayDisplay-850">Overall Review</p>
            <div className="flex items-center space-x-[1.25rem]">
              <Rating
                ratingNumber={average_product_rating}
                starClassName="w-[1.313rem] h-[1.213rem]"
              />
              <p className="text-[1.125rem] text-grayDisplay-700">
                {average_product_rating} out of 5.00
              </p>
            </div>
          </div>
        )}
      </div>

      {reviewList?.length > 0 ? (
        <>
          <HorizontalLine className="my-[2.5rem]" />
          <div className="space-y-[1.875rem]">
            {reviewList.map((reviewData, index) => (
              <div key={index} className="space-y-[1.875rem]">
                <ProductReview reviewData={reviewData} />
                <HorizontalLine />
              </div>
            ))}
          </div>
          {totalItems > perPage && (
            <div className="mt-[3rem] flex justify-center lg:justify-start">
              <Pagination
                total={totalPage}
                currentPage={currentPage}
                getPaginateData={handlePagination}
              />
            </div>
          )}
        </>
      ) : (
        <div className="min-h-[30vh] text-center font-bold text-2xl mt-[2rem]">
          This product has no reviews
        </div>
      )}
    </section>
  );
}

ProductReviewSection.propTypes = {
  data: PropTypes.shape({
    average_rating: PropTypes.number,
    max_rating: PropTypes.number,
    total_review_count: PropTypes.number,
    meta: PropTypes.object,
    reviews: PropTypes.array,
  }),
};

export default ProductReviewSection;
